/**
 * useRoadmapStats Hook
 * Provides easy access to roadmap statistics for UI components
 */

import { useState, useEffect, useCallback } from 'react';
import type { RoadmapStats } from '@/services/roadmapStatsService';
import RoadmapStatsService from '@/services/roadmapStatsService';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/config/supabase';

export const useRoadmapStats = (roadmapId: string) => {
  const [stats, setStats] = useState<RoadmapStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user email from auth or profile
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email) {
          setUserEmail(session.user.email);
        }
      } catch (err) {
        console.error('Error fetching user email:', err);
      }
    };

    fetchUserEmail();
  }, []);

  // Fetch stats when roadmapId or userEmail changes
  useEffect(() => {
    if (!roadmapId || !userEmail) return;

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const roadmapStats = await RoadmapStatsService.getRoadmapStats(userEmail, roadmapId);
        setStats(roadmapStats);
      } catch (err) {
        setError('Failed to load roadmap stats');
        console.error('Error loading stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [roadmapId, userEmail]);

  // Track view when component mounts
  useEffect(() => {
    if (!roadmapId || !userEmail) return;
    RoadmapStatsService.trackRoadmapView(userEmail, roadmapId);
  }, [roadmapId, userEmail]);

  // Callback to record study session
  const recordStudySession = useCallback(
    async (durationMinutes: number, itemsCompleted?: number, articlesStudied?: string[]) => {
      if (!userEmail) return;
      
      await RoadmapStatsService.recordStudySession(
        userEmail,
        roadmapId,
        durationMinutes,
        itemsCompleted,
        articlesStudied
      );

      // Refresh stats after recording
      const updatedStats = await RoadmapStatsService.getRoadmapStats(userEmail, roadmapId);
      setStats(updatedStats);
    },
    [roadmapId, userEmail]
  );

  return {
    stats,
    loading,
    error,
    recordStudySession,
    formatTime: RoadmapStatsService.formatTime,
    weeklyStudyTime: stats ? RoadmapStatsService.getWeeklyStudyTime(stats.daily_completion_records) : 0,
    monthlyStudyTime: stats ? RoadmapStatsService.getMonthlyStudyTime(stats.daily_completion_records) : 0
  };
};

/**
 * Hook for all roadmap stats of current user
 */
export const useAllRoadmapStats = () => {
  const [allStats, setAllStats] = useState<Record<string, RoadmapStats>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email) {
          setUserEmail(session.user.email);
        }
      } catch (err) {
        console.error('Error fetching user email:', err);
      }
    };

    fetchUserEmail();
  }, []);

  useEffect(() => {
    if (!userEmail) return;

    const fetchAllStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const stats = await RoadmapStatsService.getAllRoadmapStats(userEmail);
        setAllStats(stats);
      } catch (err) {
        setError('Failed to load roadmap stats');
        console.error('Error loading all stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllStats();
  }, [userEmail]);

  return {
    allStats,
    loading,
    error,
    getTotalStudyTime: () => {
      return Object.values(allStats).reduce((sum, stat) => sum + stat.total_minutes_studied, 0);
    },
    getTotalViews: () => {
      return Object.values(allStats).reduce((sum, stat) => sum + stat.times_viewed, 0);
    }
  };
};
